import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Other Achievements|Catalog|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	portal: 'SP',
	feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
	htmlAllowed: false,
};

export type SPOtherAchievementCatalogLocNames =
	| 'SPOtherAchievementCatalogTitle'
	| 'SPOtherAchievementCatalogPageDescription'
	| 'SPOtherAchievementCatalogSectionTitle'
	| 'SPOtherAchievementCatalogName'
	| 'SPOtherAchievementCatalogStatus'
	| 'SPOtherAchievementCatalogReimbursable'
	| 'SPOtherAchievementCatalogExternalId'
	| 'SPOtherAchievementCatalogVendor'
	| 'SPOtherAchievementCatalogUpdated'
	| 'SPOtherAchievementCatalogEditAction'
	| 'SPOtherAchievementCatalogActivateAction'
	| 'SPOtherAchievementCatalogDeactivateAction'
	| 'SPOtherAchievementCatalogActive'
	| 'SPOtherAchievementCatalogInactive'
	| 'SPOtherAchievementCatalogAddAction'
	| 'SPOtherAchievementCatalogAddSuccess'
	| 'SPOtherAchievementCatalogEditSuccess'
	| 'SPOtherAchievementCatalogActivateSuccess'
	| 'SPOtherAchievementCatalogDeactivateSuccess';

export const spOtherAchievementCatalogListLocs: LocDefs<SPOtherAchievementCatalogLocNames> = {
	SPOtherAchievementCatalogTitle: {
		defaultValue: 'Other Achievements Catalog',
		description: 'Title for the other achievements catalog page',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPOtherAchievementCatalogPageDescription: {
		defaultValue: '',
		description: 'Content displayed at the top of the other achievements catalog page',
		displayPath: `${pathStart}Page Description`,
		displayType: 'Content',
		shared: false,
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
		htmlAllowed: true,
	},
	SPOtherAchievementCatalogSectionTitle: {
		defaultValue: 'Other Achievements',
		description: 'Title displayed above the table',
		displayPath: `${pathStart}Table Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPOtherAchievementCatalogName: {
		defaultValue: 'Achievement Name',
		description: 'Label for the achievement name field',
		displayPath: `${pathStart}Name Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogStatus: {
		defaultValue: 'Status',
		description: 'Label for the status field',
		displayPath: `${pathStart}Status Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogReimbursable: {
		defaultValue: 'Reimbursable',
		description: 'Label for the reimbursable field',
		displayPath: `${pathStart}Reimbursable Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogExternalId: {
		defaultValue: 'External ID',
		description: 'Label for the external ID field',
		displayPath: `${pathStart}External ID Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogVendor: {
		defaultValue: 'Vendor',
		description: 'Label for the vendor field',
		displayPath: `${pathStart}Vendor Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogUpdated: {
		defaultValue: 'Last Updated',
		description: 'Label for the last updated field',
		displayPath: `${pathStart}Last Updated Field`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPOtherAchievementCatalogEditAction: {
		defaultValue: 'Edit',
		description: 'Edit button label',
		displayPath: `${pathStart}Edit`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPOtherAchievementCatalogActivateAction: {
		defaultValue: 'Enable',
		description: 'Enable button label',
		displayPath: `${pathStart}Enable Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPOtherAchievementCatalogDeactivateAction: {
		defaultValue: 'Disable',
		description: 'Disable button label',
		displayPath: `${pathStart}Disable Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPOtherAchievementCatalogActive: {
		defaultValue: 'Active',
		description: 'Displayed value for the active status',
		displayPath: `${pathStart}Active Status`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPOtherAchievementCatalogInactive: {
		defaultValue: 'Inactive',
		description: 'Displayed value for the inactive status',
		displayPath: `${pathStart}Inactive Status`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPOtherAchievementCatalogAddAction: {
		defaultValue: 'Add Achievement',
		description: 'Add button label',
		displayPath: `${pathStart}Add`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPOtherAchievementCatalogAddSuccess: {
		defaultValue: 'Achievement successfully added',
		description: 'Message displayed when an achievement has successfully been added',
		displayPath: `${pathStart}Add Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPOtherAchievementCatalogEditSuccess: {
		defaultValue: 'Achievement successfully edited',
		description: 'Message displayed when an achievement has successfully been edited',
		displayPath: `${pathStart}Edit Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPOtherAchievementCatalogDeactivateSuccess: {
		defaultValue: 'Achievement successfully deactivated',
		description: 'Message displayed when an achievement has successfully been deactivated',
		displayPath: `${pathStart}Deactivate Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPOtherAchievementCatalogActivateSuccess: {
		defaultValue: 'Achievement successfully activated',
		description: 'Message displayed when an achievement has successfully been activated',
		displayPath: `${pathStart}Activate Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPOtherAchievementCatalogLocType = LocMap<SPOtherAchievementCatalogLocNames>;
